import { FC } from "react";
import SharingDialog, { SharingDialogApi } from "../../components/sharing/SharingDialog";
import useI18n from "../../hooks/useTranslations";
import { useCalculationSharingApi } from "../../http/useCalculationSharing";
import { useCalculationSharingInvitesApi } from "../../http/useCalculationSharingInvites";
import { useSharingRequestsApi } from "../../http/useSharingRequests";

interface CalculationSharingDialogProps {
  calculationId: string;
  calculationName?: string;
  isAdmin?: boolean;
  onClose: () => void;
}

const CalculationSharingDialog: FC<CalculationSharingDialogProps> = ({
  calculationId,
  calculationName,
  isAdmin = false,
  onClose,
}) => {
  const i18n = useI18n();
  const calculationSharingApi = useCalculationSharingApi();
  const calculationSharingInvitesApi = useCalculationSharingInvitesApi();
  const sharingRequestsApi = useSharingRequestsApi("calculations");

  const api: SharingDialogApi = {
    useSharings: (query, signal) =>
      calculationSharingApi.useSearchCalculationSharings(calculationId, query, signal, isAdmin),
    useInvites: (query, signal) =>
      calculationSharingInvitesApi.useCalculationSharingInvites(calculationId, query, signal, isAdmin),
    insertSharing: (body) => calculationSharingApi.insertCalculationSharing(calculationId, body, isAdmin),
    updateSharing: (sharing, edit) =>
      calculationSharingApi.updateCalculationSharing(calculationId, sharing.user.id, { edit }, isAdmin),
    deleteSharing: (sharing) => calculationSharingApi.deleteCalculationSharing(calculationId, sharing.user.id, isAdmin),
    resendInvite: (inviteId) =>
      calculationSharingInvitesApi.resendCalculationSharingInvite(calculationId, inviteId, isAdmin),
    cancelInvite: (inviteId) =>
      calculationSharingInvitesApi.cancelCalculationSharingInvite(calculationId, inviteId, isAdmin),
    deleteInvite: (inviteId) =>
      calculationSharingInvitesApi.deleteCalculationSharingInvite(calculationId, inviteId, isAdmin),
    useRequests: (query, signal) => sharingRequestsApi.useSharingRequests(calculationId, query, signal, isAdmin),
    acceptRequest: (requestId, edit) =>
      sharingRequestsApi.acceptSharingRequest(calculationId, requestId, edit, isAdmin),
    rejectRequest: (requestId) => sharingRequestsApi.rejectSharingRequest(calculationId, requestId, isAdmin),
  };

  return (
    <SharingDialog
      api={api}
      labels={{
        title: calculationName
          ? `${i18n.translation.calculations.editSharings}: ${calculationName}`
          : i18n.translation.calculations.editSharings,
        shareWithUser: i18n.translation.calculations.shareWithUser,
        invitations: i18n.translation.calculations.invitations,
        unshareQuestion: i18n.translation.calculations.questions.unshare,
      }}
      onClose={onClose}
    />
  );
};

export default CalculationSharingDialog;
